import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';

export const Root = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// ===== Section =====

export const SectionRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: 576px) {
    gap: 12px;
  }
`;

export const SectionHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 0;
  user-select: none;
`;

// 캐럿 색상 — 섹션 타입별 분기. 상위=Caution 빨강, 하위=Portal Point 파랑, 관련=Gray8 회색.
export const SectionCaret = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: 'parents' | 'children' | 'related' }>(({ tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 12,
  height: 12,
  marginRight: 6,
  flexShrink: 0,
  color:
    tone === 'parents'
      ? '#f55060'
      : tone === 'children'
      ? '#0066cc'
      : '#4d5157',
}));

// ===== SearchBar =====

export const SearchBarRoot = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 12px;
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 8px;
  background-color: var(--dk-bg, #fff);

  &:focus-within {
    border-color: #000;
  }
`;

export const SearchInputField = styled('input')`
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: var(--dk-text, #000);

  &::placeholder {
    color: var(--dk-text-dim, #b7bbc2);
  }
`;

export const SearchInputActions = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const SearchAddButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--dk-text-strong, #4d5157);
  cursor: pointer;

  &:hover {
    color: var(--dk-text, #000);
  }
`;

// close-circle.svg — hover 시 SVG 의 --bg-color 만 어둡게
export const RoundedIconButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    --bg-color: #b7bbc2;
  }
`;

// ===== ResultLayer / Empty =====

// 검색 결과 드롭다운 레이어
export const ResultListBox = styled(Box)`
  display: flex;
  flex-direction: column;
  max-height: 360px;
  overflow-y: auto;
  padding: 14px 8px;
  background-color: var(--dk-bg, #fff);
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 6px;
  box-shadow: 0 16px 32px 0 rgba(0, 0, 0, 0.08);

  @media (max-width: 575.98px) {
    border-color: #000;
  }

  @media (min-width: 576px) {
    padding: 16px 8px;
  }
`;

// 빈 결과 안내 — dropdown 박스 안에 회색 텍스트로 표시 (라이브 화면 기준).
export const EmptyMessage = styled(Typography)`
  padding: 16px 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: var(--dk-text-dim, #b7bbc2);

  @media (min-width: 576px) {
    padding: 16px 20px;
  }
`;

// 검색 로딩 — 박스 안에 Spinner를 가운데 정렬.
export const LoadingMessage = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 20px;
`;

// ===== Selected list =====

export const SelectedListRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// 결과 row 우측 새창 열기 버튼 — row 클릭과 분리.
export const NewWindowIconButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--dk-text-secondary, #888d96);
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: var(--dk-text-strong, #4d5157);
  }
`;

// ===== RelatedItemRow (공통 항목 행) =====
// base=모바일(<576px). chrome 은 각 화면이 className 으로 주입 — Row 는 표현+반응형만.

// stack(패널군)=모바일 세로 스택→PC 가로 / inline(변경이력)=항상 가로
export const RowRoot = styled(Box, {
  shouldForwardProp: (p) => p !== 'layout',
})<{ layout?: 'stack' | 'inline' }>(({ layout = 'stack' }) =>
  layout === 'inline'
    ? {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 8,
        width: '100%',
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        width: '100%',
        overflow: 'hidden',
        '@media (min-width: 576px)': {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        },
      },
);

// title 묶음(좌). inline 에선 leading 아이콘 포함.
export const RowLead = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
`;

// 제목 — titleTone 으로 색/underline 분기.
// 'as' 는 반드시 걸러야 함 — 안 거르면 DOM 속성으로 흘러 태그가 안 바뀌고 링크 클릭이 죽는다.
export const RowTitle = styled('span', {
  shouldForwardProp: (p) =>
    p !== 'titleTone' && p !== 'strikethrough' && p !== 'as',
})<{
  titleTone?: 'view' | 'history' | 'plain';
  strikethrough?: boolean;
}>(({ titleTone = 'plain', strikethrough }) => {
  // 앱 다크 모드(html[data-color-mode='dark'])에선 의미 토큰으로 — 라이트 값은 fallback 그대로
  const color =
    titleTone === 'history'
      ? 'var(--dk-accent, #0066cc)'
      : 'var(--dk-text, #000)';
  // 변경이력만 상시 밑줄 — 조회·작성 제목은 hover 에서도 밑줄 없음
  const decoration =
    titleTone === 'history'
      ? strikethrough
        ? 'underline line-through'
        : 'underline'
      : strikethrough
      ? 'line-through'
      : 'none';
  return {
    flex: '1 1 auto',
    minWidth: 0,
    maxWidth: '100%',
    fontFamily: 'Pretendard, sans-serif',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    color,
    textDecoration: decoration,
    textDecorationSkipInk: 'none',
    textUnderlinePosition: 'from-font',
    cursor: titleTone === 'plain' ? 'default' : 'pointer',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    '@media (min-width: 576px)': {
      display: 'block',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
    '&:hover': {
      // history 링크는 hover 시 더 진한 파랑으로 — 구 HistoryItemTitle 동작 보존.
      color: titleTone === 'history' ? 'var(--dk-accent, #004c99)' : color,
    },
    '&:focus-visible': {
      outline: '2px solid var(--dk-text, #000)',
      outlineOffset: 2,
    },
  };
});

// badge + author/date + trailing 묶음(우)
export const RowMeta = styled(Box, {
  shouldForwardProp: (p) => p !== 'layout',
})<{ layout?: 'stack' | 'inline' }>(({ layout = 'stack' }) =>
  layout === 'inline'
    ? {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
      }
    : {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        width: '100%',
        justifyContent: 'space-between',
        '@media (min-width: 576px)': {
          display: 'inline-flex',
          width: 'auto',
          flexShrink: 0,
        },
      },
);

// 좌측 클러스터(badge) — 모바일 space-between 좌측 고정.
export const RowMetaCluster = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

// 우측 그룹(작성자 묶음 + trailing) — 모바일 space-between 우측 묶음.
// trailing 과의 간격은 화면별로 다름 — chrome 이 .related-row-meta-trailing 로 덮음.
export const RowMetaTrailing = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

// 작성자명·디바이더·작성일 묶음 — 간격은 작성/조회 공통 12px.
export const RowMetaInfo = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const RowAuthor = styled('span')`
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: var(--dk-text-secondary, #888d96);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// 조회(view)는 높이만 그 외와 분기 — 좌우 간격은 RowMetaInfo gap 이 담당(margin 금지).
export const RowDivider = styled('span', {
  shouldForwardProp: (p) => p !== 'tone',
})<{ tone?: 'view' | 'default' }>(({ tone = 'default' }) => ({
  width: 1,
  height: tone === 'view' ? 10 : 12,
  backgroundColor: 'var(--dk-bg-strong, #d4d7db)',
  flexShrink: 0,
}));

export const RowDate = styled('span')`
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: var(--dk-text-secondary, #888d96);
  white-space: nowrap;
`;

// below 슬롯(변경이력 "다시 연결" chip 행) — 제목 행 아래 줄.
export const RowBelow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
`;

// ===== chrome 변형 (styled(RowRoot)) — 화면별 bg/border/padding/hover 만 덧입힘 =====

// 검색 결과 행 — 행 전체 클릭.
export const ResultRowChrome = styled(RowRoot)`
  min-height: 39px;
  padding: 10px;
  border-radius: 6px;
  background-color: transparent;
  cursor: pointer;

  // 박스 스크롤 시 행이 압축돼 모바일 2줄째가 클립됨 — shrink 금지로 자연 높이 유지
  flex-shrink: 0;

  // 포커스를 파란 outline 대신 회색 배경으로 표시.
  &:hover,
  &:focus,
  &:focus-visible,
  &:focus-within,
  &:active {
    outline: none;
    background-color: rgba(220, 220, 220, 0.3);
  }
`;

// 등록 카드 공통 chrome — 작성(SelectedRowChrome)/조회(ViewRowChrome) 공용 바탕·외곽선
export const CardRowChrome = styled(RowRoot)`
  padding: 12px;
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 8px;
  background-color: var(--dk-bg-subtle, #f9fafb);
`;

// 작성화면만 삭제(X) 버튼과 24px — 조회는 공통 4px 유지
export const SelectedRowChrome = styled(CardRowChrome)`
  min-height: 44px;

  & .related-row-meta-trailing {
    gap: 24px;
  }
`;
